import {
  LinkOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history, Link, useModel } from "@umijs/max";
import { App as AntdApp } from "antd";
import React, { useEffect, useState } from "react";
import { useRou } from "@/utils/rou";
import {
  filterMenusByMainMenu,
  getMainMenuByPath,
} from "@/utils/mainMenu";
import routes from "../config/routes";
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Header,
  Question,
  SelectLang,
} from "@/components";
import {
  setMessageInstance,
  setNotificationInstance,
} from "@/utils/messageInstance";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";
const callRou = useRou();


// Helper function to find route by pathname
const findRouteByPath = (routes: any[], pathname: string): any => {
  for (const route of routes) {
    // Check if current route matches
    if (route.path === pathname) {
      return route;
    }
    // Check nested routes
    if (route.routes) {
      const found = findRouteByPath(route.routes, pathname);
      if (found) return found;
    }
  }
  return null;
};
/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    return new Promise<API.CurrentUser | undefined>((resolve) => {
      const userInfo = localStorage.getItem("currentUser");
      try {
        const User = JSON.parse(userInfo || "{}");
        if (User) {
          resolve(User as API.CurrentUser);
        } else {
          resolve(undefined);
        }
      } catch (error) {
        resolve({ name: "未知" } as API.CurrentUser);
      }
    });
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo() as API.CurrentUser;
    return {
      fetchUserInfo,
      currentUser: currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const { updateGlobalData, clearGlobalData } = useModel("contactModel");
  const { activeMainMenu, setActiveMainMenu } = useModel("menuModel");
  return {
    ...initialState?.settings,
    collapsed,
    onCollapse: setCollapsed,
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    }, 
    headerRender: () => {
      return <Header collapsed={collapsed} currentUser={initialState?.currentUser} />;
    },
    
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }

      const matchedMainMenu = getMainMenuByPath(location.pathname);
      if (matchedMainMenu !== activeMainMenu) {
        setActiveMainMenu(matchedMainMenu);
      }

      //获取当前路由名称
      const currentRoute = findRouteByPath(routes, location.pathname); 
      if (
        currentRoute &&
        currentRoute.name &&
        location.pathname.includes("/base-info/customer/")
      ) {
        updateGlobalData({
          pname: currentRoute.name,
          ppath: location.pathname + location.search,
          pkey: "contract",
        });
      } else if (
        currentRoute &&
        currentRoute.name &&
        location.pathname.includes("/base-info/customer-search")
      ) {
        clearGlobalData();
      }
    },
    menuDataRender: (menuData) => {
      const menuWithPermission = callRou.getHasMenu(menuData);  
      const filteredMenu = filterMenusByMainMenu(
        menuWithPermission,
        activeMainMenu,
      ); 
      return filteredMenu;
    },
    // 自定义菜单项渲染，防止显示链接地址
    menuItemRender: (itemProps, defaultDom) => {
      if (itemProps.isUrl || !itemProps.path) {
        return defaultDom;
      }
      // 使用 onClick 跳转而不是 <a> 标签，避免显示链接
      return (
        <span
          onClick={() => {
            history.push(itemProps.path || '/');
          }}
          style={{ cursor: 'pointer', display: 'block' }}
        >
          {defaultDom}
        </span>
      );
    },
    headerHeight: 56,
    bgLayoutImgList: [],

    // 自定义侧边栏底部渲染
    menuFooterRender: (props) => {
      return (
        <div
          style={{
            position: "relative",
            height: props?.collapsed ? "60px" : "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              right: props?.collapsed ? "50%" : "12px",
              bottom: "12px",
              transform: props?.collapsed ? "translateX(50%)" : "none",
              backgroundColor: "rgba(225, 250, 237, 1)",
              width: "24px",
              height: "24px",
              textAlign: "center",
              lineHeight: "24px",
              fontSize: "20px",
              color: "rgba(0, 144, 64, 1)",
              cursor: "pointer",
              zIndex: 999,
              borderRadius: "4px",
            }}
            onClick={() => setCollapsed(!collapsed)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0, 144, 64, 0)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(225, 250, 237, 1)";
            }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
        </div>
      );
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <AntdApp>
          <MessageInitializer />
          {children}
        </AntdApp>
      );
    },
  };
};

/**
 * 初始化全局 message 和 notification 实例
 */
const MessageInitializer: React.FC = () => {
  const { message, notification } = AntdApp.useApp();

  useEffect(() => {
    setMessageInstance(message);
    setNotificationInstance(notification);
  }, [message, notification]);

  return null;
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: "/api",
  ...errorConfig,
};
